import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getLocalTimeZone, parseDate, today } from '@internationalized/date';
import { CalendarDays } from 'lucide-react';
import { useRef, useState, } from 'react';
import { Button, DateInput, DatePicker, DateSegment, Dialog, Group, Popover, useLocale, } from 'react-aria-components';
import { cx } from './cx.js';
import { DateCalendar } from './date-calendar.js';
import { clampDate, exampleDate } from './date-logic.js';
import { definedProps, fieldClassName, fieldRootClassName, FieldFooter, FieldLabel, } from './field-parts.js';
const toCalendarDate = (iso) => iso === null || iso === undefined ? undefined : parseDate(iso);
/**
 * The empty field, unfocused (spelled out twice: Tailwind finds only whole classes): the
 * segments make way for a realistic example, as the placeholder of every other field
 * („z. B. 01.03.2015“). Focused, or once a segment is typed, the segments show (TT.MM.JJJJ) - an
 * attribute selector, as the segments' state lives inside React Aria.
 */
const segmentsClassName = 'flex flex-1 items-center tabular-nums group-[:not(:focus-within):not(:has([data-type]:not([data-type=literal]):not([data-placeholder])))]/date:opacity-0';
const exampleClassName = 'pointer-events-none absolute inset-y-0 left-0 hidden items-center truncate text-muted tabular-nums group-[:not(:focus-within):not(:has([data-type]:not([data-type=literal]):not([data-placeholder])))]/date:flex';
/** The calendar's button: 36 px with a mouse, 44 px under the finger (as `IconButton`). */
const calendarButton = 'grid size-9 shrink-0 cursor-pointer place-items-center rounded-full text-text outline-none hover:bg-fill focus-visible:outline-2 focus-visible:outline-focus max-md:size-11 [@media(pointer:coarse)]:size-11';
/**
 * A calendar date, typed segment by segment (TT.MM.JJJJ) or picked from a calendar: a press
 * into the field with the mouse shows the calendar under it while the segments keep the focus;
 * its button, Alt+↓ or a finger open it to be worked with the keyboard or by touch. Reaching
 * the field with Tab opens nothing, so typing stays undisturbed.
 */
export function DateField({ label, value, onChange, minValue, maxValue, description, errorMessage, isRequired, name, className, }) {
    const { locale } = useLocale();
    const min = toCalendarDate(minValue);
    const max = toCalendarDate(maxValue);
    const current = toCalendarDate(value);
    const start = () => current ?? clampDate(today(getLocalTimeZone()), min, max);
    const [isOpen, setOpen] = useState(false);
    const [mode, setMode] = useState('dialog');
    const [focused, setFocused] = useState(start);
    // The calendar follows a date typed while it is open.
    const [shown, setShown] = useState(value);
    if (value !== shown) {
        setShown(value);
        if (current)
            setFocused(current);
    }
    const groupRef = useRef(null);
    const popoverRef = useRef(null);
    const isPeeking = isOpen && mode === 'peek';
    const open = (how) => {
        setMode(how);
        setFocused(start());
        setOpen(true);
    };
    // Capturing: the segments handle their presses themselves and let none rise.
    const onPointerUpCapture = (event) => {
        if (isOpen || event.button !== 0)
            return;
        // The calendar's button opens it its own way.
        if (event.target instanceof Element && event.target.closest('button'))
            return;
        if (event.pointerType === 'mouse' || event.pointerType === 'pen')
            open('peek');
        else if (event.pointerType === 'touch')
            open('dialog');
    };
    const onKeyDown = (event) => {
        if (!isPeeking)
            return;
        if (event.key === 'Escape') {
            // Only the calendar closes, not a dialog the field stands in.
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
        }
        else if (event.altKey && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            setMode('dialog');
        }
    };
    // Shown beside the typing, the calendar closes once the focus leaves field and calendar.
    const onBlur = (event) => {
        if (!isPeeking)
            return;
        const next = event.relatedTarget;
        const staysInside = next instanceof Node &&
            (groupRef.current?.contains(next) === true || popoverRef.current?.contains(next) === true);
        if (!staysInside)
            setOpen(false);
    };
    const example = new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(exampleDate(min, max).toDate('UTC'));
    const calendar = (_jsx(DateCalendar, { focusedValue: focused, onFocusChange: setFocused, autoFocus: mode === 'dialog', minValue: min, maxValue: max, canClear: isRequired !== true && value !== null }));
    return (_jsxs(DatePicker, { value: current ?? null, onChange: (next) => {
            onChange(next === null ? null : next.toString());
        }, isOpen: isOpen, onOpenChange: (next) => {
            if (next)
                open('dialog');
            else
                setOpen(false);
        }, granularity: "day", firstDayOfWeek: "mon", shouldForceLeadingZeros: true, ...definedProps({ minValue: min, maxValue: max, isRequired, name }), className: cx('group', fieldRootClassName, className), children: [_jsx(FieldLabel, { label: label, isRequired: isRequired }), _jsxs(Group, { ref: groupRef, onPointerUpCapture: onPointerUpCapture, onKeyDown: onKeyDown, onBlur: onBlur, className: cx('flex h-11 cursor-text items-center', fieldClassName, 
                // The calendar's button fills the field's height under the finger.
                'pr-1 group-data-invalid:ring-2 group-data-invalid:ring-danger-fg max-md:pr-0 [@media(pointer:coarse)]:pr-0'), children: [_jsxs("div", { className: "group/date relative flex min-w-0 flex-1 items-center", children: [_jsx(DateInput, { className: segmentsClassName, children: (segment) => (_jsx(DateSegment, { segment: segment, className: "rounded-xs px-0.5 outline-none data-[type=literal]:px-0 data-focused:bg-accent-soft data-focused:text-accent-text data-placeholder:text-muted" })) }), _jsxs("span", { "aria-hidden": true, className: exampleClassName, children: ["z. B. ", example] })] }), _jsx(Button, { "aria-label": "Kalender \u00F6ffnen", onPress: () => {
                            setMode('dialog');
                        }, className: calendarButton, children: _jsx(CalendarDays, { "aria-hidden": true, className: "size-4 text-muted" }) })] }), _jsx(FieldFooter, { description: description, errorMessage: errorMessage }), _jsx(Popover, { ref: popoverRef, isNonModal: mode === 'peek', placement: "bottom end", offset: 6, className: "glass rounded-lg bg-surface/95 p-3 outline-none", children: mode === 'peek' ? (_jsx("div", { tabIndex: -1, className: "outline-none", children: calendar })) : (_jsx(Dialog, { "aria-label": label, className: "outline-none", children: calendar })) }, mode)] }));
}
//# sourceMappingURL=date-field.js.map